import { useCurrentAuth } from "core/features/auth/auth.store";
import { Box, Container, Flex } from "@chakra-ui/layout";
import { Skeleton, Tag, Text } from "@chakra-ui/react";
import { Col, Row } from "antd";
import useAprovarProcuracao from "core/features/assembleia/hooks/procuracao/useAprovarProcuracao";
import useProcuracao from "core/features/assembleia/hooks/procuracao/useProcuracao";
import useRejeitarProcuracao from "core/features/assembleia/hooks/procuracao/useRejeitarProcuracao";
import { StatusProcuracaoEnum } from "core/features/assembleia/typings/procuracao";
import { useBreadcrumb } from "core/features/breadcrumbs";
import colors from "core/resources/theme/colors";
import { showErrorAxios, showSuccess, useLoading } from "infra/helpers/alerta";
import { useState } from "react";
import { useParams } from "react-router-dom";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import CardDefault from "ui/components/Cards/CardDefault";
import { TabsDetalheAssembleiaRoutes } from "../../typings";
import {
  getTagColorSchemeForStatus,
  getTagVariantForStatus,
} from "../ListaProcuracoes/util";
import Documentos from "./components/Documentos";
import Historico from "./components/Historico";
import { PessoaInfo } from "./components/PessoaInfo";
import { CollapsePesos } from "./components/CollapsePesos";
import ModalRejeitarMotivo from "ui/components/ModalRejeitarMotivo/ModalRejeitarMotivo";

function DetalheProcuracao() {
  const { auth } = useCurrentAuth();
  const { id, idProcuracao } = useParams() as {
    id: string;
    idProcuracao: string;
  };

  const { data, isLoading, isFetching, refetch } = useProcuracao({
    idProcuracao,
    idVotacao: id,
  });

  const [rejeitarModalOpen, setRejeitarModalOpen] = useState(false);

  const { mutate: rejeitarProcuracao, isLoading: isLoadingRejeitar } =
    useRejeitarProcuracao({ idProcuracao, idVotacao: id });
  const { mutate: aprovarProcuracao, isLoading: isLoadingAprovar } =
    useAprovarProcuracao({ idProcuracao, idVotacao: id });

  const _isLoadingSave = isLoadingAprovar || isLoadingRejeitar;

  const _isLoading = isLoading || isFetching;

  const nomeProcuracao = _isLoading
    ? "Carregando..."
    : `Procuração ${
        data?.solicitante.nome ? "de " + data?.solicitante.nome : ""
      }`;
  useBreadcrumb([
    { name: "Votações", url: "/administracao/assembleias" },
    {
      name: data?.nomeVotacao || "Carregando...",
      url: `/administracao/assembleias/${id}`,
    },
    {
      name: "Procurações",
      url: `/administracao/assembleias/${id}?opcao=${TabsDetalheAssembleiaRoutes.Procuracoes}`,
    },
    {
      name: nomeProcuracao,
      url: `"/administracao/assembleias/${id}/procuracoes/${idProcuracao}`,
    },
  ]);

  useLoading(_isLoading);

  const _status =
    data?.status.id === StatusProcuracaoEnum.Pendente
      ? StatusProcuracaoEnum.EmAnalise
      : data?.status.id;

  return (
    <Container maxW="container.xl" pb={4}>
      <Flex flexDir="column" position="relative" gridGap={8}>
        <Flex justifyContent="space-between">
          <Text as="h3" fontSize={20} fontWeight="medium">
            {nomeProcuracao}
          </Text>
          {!_isLoading ? (
            <Flex gridGap={[4, 4]}>
              {_status !== StatusProcuracaoEnum.Rejeitada ? (
                <PrimaryButton
                  bgColor={colors.red}
                  disabled={
                    _isLoadingSave || !(auth.id === data?.logs[0].usuarioId)
                  }
                  isLoading={_isLoadingSave}
                  onClick={() => setRejeitarModalOpen(true)}
                >
                  Rejeitar
                </PrimaryButton>
              ) : null}
              {_status !== StatusProcuracaoEnum.Aprovada ? (
                <PrimaryButton
                  bgColor={colors.green500}
                  disabled={
                    _isLoadingSave || !(auth.id === data?.logs[0].usuarioId)
                  }
                  isLoading={_isLoadingSave}
                  onClick={() => {
                    aprovarProcuracao(undefined, {
                      onError: (e) => showErrorAxios(e),
                      onSuccess: () => {
                        showSuccess("Solicitação aprovada com sucesso.").then(
                          () => refetch()
                        );
                      },
                    });
                  }}
                >
                  Aprovar
                </PrimaryButton>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
        {auth.id !== data?.logs?.[0].usuarioId ? (
          <Flex mt={2}>
            <Text
              as="h6"
              fontSize={18}
              fontWeight="medium"
              color={colors.red500}
            >
              Somente o analista dessa procuração pode realizar alterações no
              status da mesma.
            </Text>
          </Flex>
        ) : null}

        <Flex gridGap={"24px"}>
          <CardDefault
            px={8}
            pt={8}
            w="100%"
            minH="266px"
            mt={8}
            flexDir="column"
            position="relative"
          >
            {!_isLoading ? (
              <>
                <Box mb={8}>
                  <PessoaInfo
                    titulo="Solicitante"
                    imagemUrl={data?.solicitante.imagemUrl}
                    nome={data?.solicitante.nome!}
                    celular={data?.solicitante.celular!}
                    email={data?.solicitante.email!}
                    documento={data?.solicitante.documento!}
                  />
                </Box>
              </>
            ) : (
              <Skeleton h="266px" w="100%" />
            )}
          </CardDefault>
          <CardDefault
            px={8}
            pt={8}
            w="100%"
            minH="266px"
            mt={8}
            flexDir="column"
            position="relative"
          >
            {!_isLoading ? (
              <>
                <Flex
                  justifyContent="flex-end"
                  position="absolute"
                  top={8}
                  right={8}
                >
                  <Tag
                    rounded="full"
                    px={4}
                    py={2}
                    colorScheme={getTagColorSchemeForStatus(_status!)}
                    variant={getTagVariantForStatus(_status!)}
                  >
                    {_status === StatusProcuracaoEnum.EmAnalise
                      ? "Em análise"
                      : data?.status?.nome}
                  </Tag>
                </Flex>
                <Box>
                  <PessoaInfo
                    titulo="Representante"
                    imagemUrl={data?.representante.imagemUrl}
                    nome={data?.representante.nome!}
                    celular={data?.representante.celular!}
                    email={data?.representante.email!}
                    documento={data?.representante.documento!}
                    subtitulo={
                      data?.pessoaExterna
                        ? "Pessoa Externa"
                        : "Morador do Condomínio"
                    }
                  />
                </Box>
              </>
            ) : (
              <Skeleton h="266px" w="100%" />
            )}
          </CardDefault>
        </Flex>

        <Box as={Row} gutter={24}>
          <Box as={Col} xs={12}>
            <CardDefault>
              {!_isLoading ? (
                <Historico logs={data?.logs || []} />
              ) : (
                <Skeleton h="350px" w="100%" />
              )}
            </CardDefault>
          </Box>

          <Box as={Col} xs={12}>
            <Box mb={4} w="100%">
              <CardDefault>
                {!_isLoading ? (
                  data ? (
                    <Documentos procuracao={data!} />
                  ) : null
                ) : (
                  <Skeleton h="150px" w="100%" />
                )}
              </CardDefault>
            </Box>

            <CardDefault px={_isLoading ? undefined : 0}>
              {!_isLoading ? (
                <CollapsePesos unidades={data?.unidades || []} />
              ) : (
                <Skeleton h="350px" w="100%" />
              )}
            </CardDefault>
          </Box>
        </Box>
      </Flex>

      <ModalRejeitarMotivo
        isLoadingSave={_isLoadingSave}
        visible={rejeitarModalOpen}
        onClose={() => setRejeitarModalOpen(false)}
        onSubmit={(motivo) => {
          rejeitarProcuracao(motivo, {
            onError: (e) => showErrorAxios(e),
            onSuccess: () =>
              showSuccess("Procuração rejeitada com sucesso").then(() => {
                setRejeitarModalOpen(false);
                refetch();
              }),
          });
        }}
      />
    </Container>
  );
}

export default DetalheProcuracao;
